/**
 * Route guard: redirects unauthenticated users to `/login`, and optionally
 * restricts access to a specific set of roles (rendering a 403 page instead).
 */

import { Navigate, Outlet, useLocation } from "react-router-dom";
import { CircularProgress, Box } from "@mui/material";
import useAuth from "@/hooks/useAuth";
import type { ModuleCode } from "@/types";

interface ProtectedRouteProps {
  requiredRoles?: string[];
  requiredModule?: ModuleCode;
}

export function ProtectedRoute({ requiredRoles, requiredModule }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasRole, hasModule } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (requiredRoles && !requiredRoles.some((role) => hasRole(role))) {
    return <Navigate to="/forbidden" replace />;
  }

  if (requiredModule && !hasModule(requiredModule)) {
    return <Navigate to="/forbidden" replace />;
  }

  return <Outlet />;
}

export default ProtectedRoute;
